import { Engine, KeyEvent, Keys, Label, PointerEvent, Random, Scene, vec } from 'excalibur';
import { DriftingDoodle } from '../actors/drifting-doodle';
import { MascotClock } from '../actors/mascot-clock';
import { PaperBackground } from '../actors/paper-background';
import { toRadians } from '../utils/angles';
import { sfx } from '../utils/audio';
import { DESIGN_WIDTH, SEEDS } from '../utils/constants';
import { MONETIZATION } from '../monetization/config';
import { PlacementRect, randomPointInRects } from '../utils/placement';
import { DOODLE_KINDS } from '../utils/sketch';
import { refreshSceneTheme } from '../themes/theme';
import { hasSeenTutorial, markTutorialSeen } from '../utils/storage';
import { HandLabel } from '../ui/label';
import { refreshSidePanelBest, refreshSidePanelLeaderboard } from '../ui/side-panel';
import { SketchButton } from '../ui/sketch-button';

const TITLE_DOODLE_ZONES: PlacementRect[] = [
  { left: 70, top: 36, right: 650, bottom: 214 },
  { left: 16, top: 236, right: 90, bottom: 1232 },
  { left: 630, top: 236, right: 704, bottom: 1232 },
];

export class TitleScene extends Scene {
  private prompt!: Label;
  private howToButton!: SketchButton;
  private scoresButton!: SketchButton;
  private shopButton: SketchButton | null = null;
  private time = 0;
  private leaving = false;

  onInitialize(): void {
    this.add(new PaperBackground());
    this.add(new MascotClock({ pos: vec(DESIGN_WIDTH / 2, 730) }));

    const crazy = new HandLabel({
      name: 'title-crazy',
      text: 'Crazy',
      pos: vec(DESIGN_WIDTH / 2 - 34, 298),
      size: 122,
      tone: 'ink',
    });
    crazy.rotation = toRadians(-4);
    const clock = new HandLabel({
      name: 'title-clock',
      text: 'Clock',
      pos: vec(DESIGN_WIDTH / 2 + 40, 426),
      size: 122,
      tone: 'blueDeep',
    });
    clock.rotation = toRadians(3);
    this.add(crazy);
    this.add(clock);

    this.add(
      new HandLabel({
        name: 'title-tagline',
        text: 'a doodle timing game',
        pos: vec(DESIGN_WIDTH / 2, 528),
        size: 34,
        tone: 'inkSoft',
      }),
    );

    this.prompt = new HandLabel({
      name: 'title-prompt',
      text: 'tap to start',
      pos: vec(DESIGN_WIDTH / 2, 1140),
      size: 48,
      tone: 'ink',
    });
    this.add(this.prompt);

    this.add(
      new HandLabel({
        name: 'title-hint',
        text: 'tap when a hand reaches its own dot',
        pos: vec(DESIGN_WIDTH / 2, 1076),
        size: 26,
        tone: 'inkSoft',
        maxWidth: 620,
      }),
    );

    const hasShop = MONETIZATION.iap.shop && MONETIZATION.iapProvider !== 'none';
    const buttonWidth = hasShop ? 180 : 220;
    const buttonSize = hasShop ? 26 : 28;

    this.howToButton = new SketchButton({
      name: 'title-how-to',
      text: 'how to play',
      width: buttonWidth,
      height: 56,
      accentTone: 'mint',
      fontSize: buttonSize,
      onPress: this.openTutorial,
    });
    this.howToButton.pos = vec(hasShop ? 130 : 200, 1218);
    this.add(this.howToButton);

    this.scoresButton = new SketchButton({
      name: 'title-scores',
      text: 'top scores',
      width: buttonWidth,
      height: 56,
      accentTone: 'blue',
      fontSize: buttonSize,
      onPress: this.openScores,
    });
    this.scoresButton.pos = vec(hasShop ? 360 : 520, 1218);
    this.add(this.scoresButton);

    if (hasShop) {
      const shopButton = new SketchButton({
        name: 'title-shop',
        text: 'shop',
        width: buttonWidth,
        height: 56,
        accentTone: 'pink',
        fontSize: buttonSize,
        onPress: this.openShop,
      });
      shopButton.pos = vec(590, 1218);
      this.shopButton = shopButton;
      this.add(shopButton);
    }

    const rng = new Random(SEEDS.drifting);
    for (let i = 0; i < 10; i++) {
      this.add(
        new DriftingDoodle({
          name: `title-doodle-${i}`,
          kind: DOODLE_KINDS[i % DOODLE_KINDS.length],
          pos: randomPointInRects(rng, TITLE_DOODLE_ZONES),
          size: rng.floating(13, 22),
          toneIndex: i,
          seed: SEEDS.drifting + i * 13,
        }),
      );
    }
  }

  onActivate(): void {
    this.leaving = false;
    refreshSceneTheme(this);
    refreshSidePanelBest();
    void refreshSidePanelLeaderboard();
    this.input.pointers.on('down', this.startGame);
    this.input.keyboard.on('press', this.handleKey);
  }

  onDeactivate(): void {
    this.input.pointers.off('down', this.startGame);
    this.input.keyboard.off('press', this.handleKey);
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    this.time += elapsed / 1000;
    const pulse = 1 + Math.sin(this.time * 3.2) * 0.1;
    this.prompt.scale = vec(pulse, pulse);
    this.prompt.opacity = 0.7 + Math.sin(this.time * 3.2) * 0.3;
  }

  private startGame = (evt?: PointerEvent): void => {
    if (this.leaving) {
      return;
    }
    if (evt && this.howToButton.isPointInside(evt.worldPos.x, evt.worldPos.y)) {
      return;
    }
    if (evt && this.scoresButton.isPointInside(evt.worldPos.x, evt.worldPos.y)) {
      return;
    }
    if (evt && this.shopButton && this.shopButton.isPointInside(evt.worldPos.x, evt.worldPos.y)) {
      return;
    }
    this.leaving = true;
    sfx.resume();
    sfx.start();
    if (!hasSeenTutorial()) {
      markTutorialSeen();
      void this.engine.goToScene('tutorial');
      return;
    }
    void this.engine.goToScene('game');
  };

  private openTutorial = (): void => {
    if (this.leaving) {
      return;
    }
    this.leaving = true;
    sfx.resume();
    sfx.start();
    void this.engine.goToScene('tutorial');
  };

  private openShop = (): void => {
    if (this.leaving) {
      return;
    }
    this.leaving = true;
    sfx.resume();
    sfx.start();
    void this.engine.goToScene('shop');
  };

  private openScores = (): void => {
    if (this.leaving) {
      return;
    }
    this.leaving = true;
    sfx.resume();
    sfx.start();
    void this.engine.goToScene('scores');
  };

  private handleKey = (evt: KeyEvent): void => {
    if (evt.key === Keys.Space || evt.key === Keys.Enter) {
      this.startGame();
    }
  };
}
